import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';
import { useUser } from '../hooks/useUser';
import { isActiveUser } from '../sp-types/userType';
import { getAuthConfigByEmail, isAuthMethodAllowed } from '../utils/authConfig';
import { IdleTimerProvider } from 'react-idle-timer';
import IdleTimeoutModal from '../components/Auth/IdleTimeoutModal';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard - Protects routes that require authentication
 * Checks: valid session UID + user exists + user is active
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const hasChecked = useRef(false);
  const { data: user, isLoading, isError } = useUser();

  useEffect(() => {
    // Skip if already authorized
    if (hasChecked.current && isAuthorized) return;

    const checkAuth = () => {
      // Check session
      const uid = sessionStorage.getItem('uid');
      if (!uid || uid === 'undefined') {
        console.log('🚪 [AUTH GUARD] No valid session');
        sessionStorage.removeItem('uid');
        setIsChecking(false);
        navigate('/login');
        return;
      }

      // Wait for user data
      if (isLoading) return;

      // Check user exists
      if (isError || !user) {
        console.error('❌ [AUTH GUARD] User not found');
        sessionStorage.removeItem('uid');
        setIsChecking(false);
        navigate('/login');
        return;
      }

      // Check user is active
      if (!isActiveUser(user)) {
        console.error('❌ [AUTH GUARD] User not active:', user.status);
        const statusMsg = user.status === 'pending'
          ? 'Your account is pending approval. Please contact your administrator.'
          : user.status === 'suspended'
            ? 'Your account has been suspended. Please contact your administrator.'
            : user.status === 'deactivated'
              ? 'Your account has been deactivated.'
              : 'Your account is not active. Please contact your administrator.';
        setBlockedMessage(statusMsg);
        setIsChecking(false);
        // Sign out from Firebase
        signOut(auth).catch(console.error);
        sessionStorage.removeItem('uid');
        return;
      }

      // Check accountId exists
      if (!user.accountId) {
        console.error('❌ [AUTH GUARD] No accountId');
        setBlockedMessage('Your account is not associated with an organization. Please contact your administrator.');
        setIsChecking(false);
        signOut(auth).catch(console.error);
        sessionStorage.removeItem('uid');
        return;
      }

      // Check auth method is allowed by account (async check)
      checkAuthMethodAllowed();
    };

    const checkAuthMethodAllowed = async () => {
      if (!user?.email || !user?.authProvider) {
        // If no auth provider info, allow (legacy users)
        console.log('✅ [AUTH GUARD] Authorized (legacy user):', user?.email);
        hasChecked.current = true;
        setIsAuthorized(true);
        setIsChecking(false);
        return;
      }

      try {
        const accountConfig = await getAuthConfigByEmail(user.email);

        // Check if SSO is required but user didn't use SSO
        if (accountConfig.requireSSO && user.authProvider !== 'sso') {
          console.error('❌ [AUTH GUARD] Account requires SSO but user auth provider is:', user.authProvider);
          setBlockedMessage(`Your organization requires SSO login. Please log out and sign in again using ${accountConfig.ssoProvider?.name || 'Company SSO'}.`);
          setIsChecking(false);
          signOut(auth).catch(console.error);
          sessionStorage.removeItem('uid');
          return;
        }

        // Check if auth method is in allowed list
        if (!isAuthMethodAllowed(accountConfig, user.authProvider as any)) {
          console.error('❌ [AUTH GUARD] Auth method not allowed:', user.authProvider);
          setBlockedMessage('Your login method is not allowed for your organization. Please contact your administrator.');
          setIsChecking(false);
          signOut(auth).catch(console.error);
          sessionStorage.removeItem('uid');
          return;
        }

        console.log('✅ [AUTH GUARD] Authorized:', user.email);
        hasChecked.current = true;
        setIsAuthorized(true);
        setIsChecking(false);
      } catch (error) {
        console.error('❌ [AUTH GUARD] Error checking auth config:', error);
        // On error, allow access (fail open for now)
        hasChecked.current = true;
        setIsAuthorized(true);
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [user, isLoading, isError, isAuthorized]);

  // Show blocked message if user is not allowed
  if (blockedMessage) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{blockedMessage}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Show nothing while checking (prevents flash)
  if (isChecking || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return isAuthorized ? <>{children}</> : null;
};

export default AuthGuard;
